import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/api";
import { getAttribution, funnelEvent } from "@/lib/attribution";
import { pixelTrack } from "@/lib/pixels";

const NAVY = "#132a63";
const GREEN = "#8CE313";
const LOGO = "/angel-logo-white.png";

const BENEFICIOS = [
  { icon: "fa-user-md", t: "Consulta médica", d: "Por llamada o videollamada, cuando la necesites." },
  { icon: "fa-brain", t: "Asistencia psicológica", d: "Apoyo emocional para ti y tu familia." },
  { icon: "fa-apple-alt", t: "Asistencia nutricional", d: "Orientación para comer mejor y más sano." },
  { icon: "fa-scale-balanced", t: "Orientación legal", d: "Resuelve tus dudas con abogados." },
  { icon: "fa-hands-holding-child", t: "Apoyo familiar", d: "Acompañamiento para los tuyos." },
  { icon: "fa-users", t: "Beneficiarios familiares", d: "Protege también a tu familia." },
  { icon: "fa-tags", t: "App de descuentos", d: "Descuentos en farmacias, laboratorios y más. Incluida con tu membresía." },
  { icon: "fa-mobile-screen", t: "Desde tu celular", d: "Todo a un mensaje o llamada de distancia." },
  { icon: "fa-tag", t: "Precio accesible", d: "Desde $80 al mes con 12 MSI. Sin contratos forzosos." },
];

const FAQS = [
  { q: "¿Cómo funciona?", a: "Te registras, pagas tu membresía y de inmediato puedes usar la línea de asistencia médica, psicológica, nutricional y legal desde tu celular." },
  { q: "¿Puedo incluir a mi familia?", a: "Sí. Puedes agregar beneficiarios para que ellos también reciban apoyo." },
  { q: "¿Tiene permanencia?", a: "No. La membresía mensual la cancelas cuando quieras." },
  { q: "¿Qué formas de pago hay?", a: "Mensual $120, anual $800 (2 meses gratis) o 12 meses sin intereses de $80 con tu tarjeta de crédito." },
  { q: "¿Los servicios tienen tiempo de espera?", a: "Por transparencia: la grúa/asistencia vial y la ambulancia están disponibles a los 30 días de activar tu membresía, la consulta médica a los 10 días, y la asistencia legal a los 10 días para problemas automovilísticos o 30 días para otros casos. La asistencia psicológica, nutricional y la app de descuentos se activan de inmediato." },
];

const PLANS = [
  { type: "msi12", short: "12 MSI", title: "12 meses sin intereses", price: "$80", unit: "/mes x 12", total: "$960 total", note: "Anualidad a 12 MSI con tarjeta de crédito", best: true },
  { type: "anual", short: "Anual", title: "Pago anual", price: "$800", unit: "/año", total: "2 meses gratis", note: "Un solo pago, vigencia 12 meses" },
  { type: "mensual", short: "Mensual", title: "Mensual", price: "$120", unit: "/mes", total: "Renovación automática", note: "Precio normal · Cancela cuando quieras" },
];

const BTN_LABEL = {
  msi12: "Pagar y activar — 12 x $80 MSI",
  anual: "Pagar y activar — $800/año",
  mensual: "Pagar y activar — $120/mes",
};

export default function Funnel() {
  const [form, setForm] = useState({
    nombre: "", celular: "", email: "", ciudad: "", estado: "",
    num_beneficiarios: 0, fecha_nacimiento: "",
    acepta_terminos: false, acepta_privacidad: false, acepta_mensajes: false,
    codigo_promo: "", codigo_vendedor: "",
  });
  const [loading, setLoading] = useState(false);
  const [planType, setPlanType] = useState("msi12");
  const [addons, setAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const formRef = useRef(null);
  const [params] = useSearchParams();
  const toggleAddon = (code) => setSelectedAddons((s) => s.includes(code) ? s.filter((c) => c !== code) : [...s, code]);

  useEffect(() => {
    funnelEvent("page_view");
    apiClient.get("/funnel/addons").then(({ data }) => setAddons(data)).catch(() => {});
    const attr = getAttribution();
    if (attr.ref) setForm((f) => ({ ...f, codigo_vendedor: attr.ref }));
    // Recuperación de carrito: si viene ?lead=, precargar datos
    const leadId = params.get("lead");
    if (leadId) {
      apiClient.get(`/funnel/lead/${leadId}`).then(({ data }) => {
        setForm((f) => ({
          ...f,
          nombre: data.nombre || f.nombre, celular: data.celular || f.celular,
          email: data.email || f.email, ciudad: data.ciudad || f.ciudad,
          estado: data.estado || f.estado, num_beneficiarios: data.num_beneficiarios || 0,
        }));
        toast.success("Retomamos tu registro. Solo confirma y activa. 🙌");
        setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 400);
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const validCel = (v) => v.replace(/\D/g, "").length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.celular || !form.email || !form.ciudad || !form.estado) {
      toast.error("Completa todos los campos obligatorios."); return;
    }
    if (!validEmail(form.email)) { toast.error("Email inválido."); return; }
    if (!validCel(form.celular)) { toast.error("Celular inválido (mínimo 10 dígitos)."); return; }
    if (!form.acepta_terminos || !form.acepta_privacidad) {
      toast.error("Debes aceptar términos y aviso de privacidad."); return;
    }
    setLoading(true);
    try {
      const attr = getAttribution();
      const { data: lead } = await apiClient.post("/funnel/lead", {
        ...form,
        num_beneficiarios: Number(form.num_beneficiarios) || 0,
        ref: attr.ref || form.codigo_vendedor || null,
        utm_source: attr.utm_source || null,
        utm_campaign: attr.utm_campaign || null,
        utm_ad: attr.utm_ad || null,
        landing_url: window.location.href,
      });
      funnelEvent("lead_created", { lead_id: lead.lead_id });
      pixelTrack("lead", { eventId: lead.lead_id });
      const { data: ck } = await apiClient.post("/funnel/checkout", {
        lead_id: lead.lead_id,
        origin_url: window.location.origin,
        plan_type: planType,
        addons: selectedAddons,
      });
      funnelEvent("payment_started", { lead_id: lead.lead_id, amount: ck.amount });
      pixelTrack("checkout", { value: ck.amount, eventId: ck.session_id });
      window.location.href = ck.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const input = { width: "100%", padding: "13px 14px", borderRadius: 12, border: "1.5px solid #d7dee8", fontSize: 15, boxSizing: "border-box", fontFamily: "inherit" };
  const label = { fontSize: 13, fontWeight: 600, color: NAVY, marginBottom: 4, display: "block" };

  return (
    <div style={{ fontFamily: "'Poppins', system-ui, sans-serif", color: "#1f2937", background: "#fff" }}>
      <Helmet>
        <title>Mi Ángel Asís — Protege a tu familia por solo $80 al mes</title>
        <meta name="description" content="Asistencia médica, psicológica, nutricional, legal y familiar desde tu celular por solo $80 al mes. Activa tu protección ahora." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      {/* HERO */}
      <section style={{ background: NAVY, color: "white", padding: "28px 20px 40px", textAlign: "center" }}>
        <img src={LOGO} alt="Mi Ángel Asís" style={{ height: 64, marginBottom: 14 }} />
        <h1 style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.15, margin: "0 auto 8px", maxWidth: 620 }}>
          Protege a tu familia desde <span style={{ color: GREEN }}>$80 al mes</span>
        </h1>
        <p style={{ fontSize: 12.5, opacity: 0.82, maxWidth: 540, margin: "0 auto 14px", lineHeight: 1.4 }}>
          *Pagando la anualidad a <strong>12 meses sin intereses</strong> con tarjeta de crédito. Precio mensual normal: <strong>$120/mes</strong>.
        </p>
        <p style={{ fontSize: 17, opacity: 0.92, maxWidth: 560, margin: "0 auto 22px" }}>
          Con Mi Ángel Asís tienes asistencia médica, psicológica, nutricional, legal y familiar desde tu celular.
        </p>
        <button data-testid="hero-cta" onClick={scrollToForm}
          style={{ background: GREEN, color: NAVY, border: "none", padding: "16px 30px", borderRadius: 999, fontSize: 17, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 24px rgba(140,227,19,0.4)" }}>
          Activar mi protección ahora
        </button>
        <div style={{ marginTop: 16, fontSize: 13, opacity: 0.8 }}>
          <i className="fas fa-lock" /> Pago seguro · Sin permanencia · Cancela cuando quieras
        </div>
      </section>

      {/* PROBLEMA */}
      <section style={{ padding: "38px 20px", maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: NAVY }}>Cuando pasa una emergencia, ¿a quién llamas?</h2>
        <p style={{ fontSize: 16, color: "#4b5563", marginTop: 10 }}>
          En una urgencia médica, un problema legal o una duda familiar, la mayoría no sabe <strong>a quién llamar</strong>,
          <strong> cuánto va a costar</strong> ni <strong>dónde recibir orientación confiable</strong>. Perder tiempo puede salir muy caro.
        </p>
      </section>

      {/* SOLUCION */}
      <section style={{ padding: "38px 20px", background: "#F3F7FF" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: NAVY }}>Mi Ángel Asís es tu línea de apoyo</h2>
          <p style={{ fontSize: 16, color: "#4b5563", marginTop: 10 }}>
            Una membresía mensual accesible para que tú y tus beneficiarios tengan asistencia médica, psicológica,
            nutricional y legal, siempre a la mano. <strong>Tranquilidad para toda la familia, desde tu celular.</strong>
          </p>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section style={{ padding: "38px 20px", maxWidth: 900, margin: "0 auto" }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: NAVY, textAlign: "center", marginBottom: 22 }}>Todo lo que incluye tu membresía</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 }}>
          {BENEFICIOS.map((b, i) => (
            <div key={i} style={{ background: "white", border: "1px solid #eef2f7", borderRadius: 16, padding: 18, boxShadow: "0 2px 10px rgba(19,42,99,0.05)" }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: "#EAF6D6", color: "#5BA628", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, marginBottom: 10 }}>
                <i className={`fas ${b.icon}`} />
              </div>
              <div style={{ fontWeight: 700, color: NAVY }}>{b.t}</div>
              <div style={{ fontSize: 14, color: "#6b7280", marginTop: 4 }}>{b.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HISTORIAS / CASOS REALES */}
      <section style={{ background: NAVY, padding: "44px 20px" }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: "white", textAlign: "center", marginBottom: 6 }}>
          Cuando más lo necesitas, <span style={{ color: GREEN }}>ahí estamos</span>
        </h2>
        <p style={{ textAlign: "center", color: "rgba(255,255,255,0.85)", marginBottom: 28, fontSize: 15, maxWidth: 560, margin: "0 auto 28px" }}>
          Situaciones reales donde tener Mi Ángel Asís hace toda la diferencia.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 20, maxWidth: 1000, margin: "0 auto" }}>
          {[
            { img: "/ilustraciones/vial.webp", cap: "Asistencia vial", desc: "Tu auto te dejó a pie. Una llamada y llega la grúa." },
            { img: "/ilustraciones/medico.webp", cap: "Asistencia médica", desc: "Orientación de un médico justo cuando lo necesitas." },
            { img: "/ilustraciones/legal.webp", cap: "Respaldo legal", desc: "Un abogado que defiende tus derechos y te tranquiliza." },
          ].map((h, i) => (
            <figure key={i} data-testid={`historia-${i}`} style={{ margin: 0, background: "white", borderRadius: 18, overflow: "hidden", boxShadow: "0 12px 30px rgba(0,0,0,0.28)" }}>
              <img src={h.img} alt={h.cap} loading="lazy" style={{ width: "100%", display: "block", aspectRatio: "4/5", objectFit: "cover" }} />
              <figcaption style={{ padding: "16px 18px 20px" }}>
                <div style={{ fontWeight: 800, color: NAVY, fontSize: 17 }}>{h.cap}</div>
                <div style={{ color: "#5b6472", fontSize: 14, marginTop: 4 }}>{h.desc}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* PLANES */}
      <section style={{ padding: "10px 20px 40px" }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: NAVY, textAlign: "center", marginBottom: 6 }}>Elige tu plan</h2>
        <p style={{ textAlign: "center", color: "#6b7280", marginBottom: 24, fontSize: 14 }}>Todos los precios son + IVA · Sin permanencia</p>
        <div data-testid="plan-selector" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 16, maxWidth: 860, margin: "0 auto" }}>
          {PLANS.map((p) => {
            const active = planType === p.type;
            return (
              <div key={p.type} data-testid={`plan-card-${p.type}`} onClick={() => setPlanType(p.type)}
                style={{ position: "relative", cursor: "pointer", border: `2px solid ${active ? GREEN : "#e6ecf5"}`, borderRadius: 18, padding: "24px 20px 20px", background: "white", boxShadow: active ? "0 10px 28px rgba(140,227,19,0.25)" : "0 2px 10px rgba(19,42,99,0.05)", transition: "border-color .2s, box-shadow .2s, transform .2s", transform: active ? "translateY(-2px)" : "none" }}>
                {p.best && <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: GREEN, color: NAVY, fontSize: 11, fontWeight: 800, padding: "4px 14px", borderRadius: 999, whiteSpace: "nowrap" }}>MÁS POPULAR</div>}
                <div style={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>{p.title}</div>
                <div style={{ fontSize: 40, fontWeight: 800, color: NAVY, lineHeight: 1.1, marginTop: 6 }}>{p.price}<span style={{ fontSize: 15, fontWeight: 600 }}>{p.unit}</span></div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#5BA628", marginTop: 2 }}>{p.total}</div>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 8, minHeight: 34 }}>{p.note}</div>
                <div style={{ marginTop: 12, textAlign: "center", fontWeight: 700, fontSize: 14, color: active ? NAVY : "#9ca3af" }}>
                  <i className={`fas ${active ? "fa-circle-check" : "fa-circle"}`} style={{ color: active ? GREEN : "#cbd5e1", marginRight: 6 }} />
                  {active ? "Seleccionado" : "Elegir este plan"}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ textAlign: "center", marginTop: 26 }}>
          <button data-testid="precio-cta" onClick={scrollToForm}
            style={{ background: NAVY, color: "white", border: "none", padding: "15px 34px", borderRadius: 999, fontSize: 16, fontWeight: 800, cursor: "pointer" }}>
            Continuar con el plan seleccionado
          </button>
        </div>
      </section>

      {/* CONFIANZA / FAQ */}
      <section style={{ padding: "34px 20px", background: "#F3F7FF" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: NAVY, textAlign: "center", marginBottom: 18 }}>Preguntas frecuentes</h2>
          {FAQS.map((f, i) => (
            <div key={i} style={{ background: "white", borderRadius: 12, padding: "14px 16px", marginBottom: 10, border: "1px solid #e6ecf5" }}>
              <div style={{ fontWeight: 700, color: NAVY }}>{f.q}</div>
              <div style={{ fontSize: 14, color: "#4b5563", marginTop: 4 }}>{f.a}</div>
            </div>
          ))}
          <p style={{ textAlign: "center", fontSize: 13, color: "#6b7280", marginTop: 14 }}>
            <a href="/privacidad" style={{ color: NAVY }}>Aviso de privacidad</a> ·{" "}
            <a href="/terminos" style={{ color: NAVY }}>Términos y condiciones</a>
          </p>
        </div>
      </section>

      {/* FORMULARIO */}
      <section ref={formRef} style={{ padding: "40px 20px 60px" }}>
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: NAVY, textAlign: "center" }}>Activa tu protección</h2>
          <p style={{ textAlign: "center", color: "#6b7280", marginBottom: 18 }}>Solo toma 1 minuto. Tu membresía se activa una vez confirmado el pago. Aplican términos y periodos de carencia según el servicio.</p>
          <form onSubmit={submit} style={{ display: "grid", gap: 14 }}>
            <div>
              <label style={label}>Plan seleccionado *</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {PLANS.map((p) => {
                  const active = planType === p.type;
                  return (
                    <button type="button" key={p.type} data-testid={`form-plan-${p.type}`} onClick={() => setPlanType(p.type)}
                      style={{ cursor: "pointer", border: `2px solid ${active ? GREEN : "#d7dee8"}`, background: active ? "#F4FCE8" : "white", borderRadius: 12, padding: "10px 4px", textAlign: "center", transition: "border-color .2s, background .2s" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: NAVY }}>{p.short}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: NAVY }}>{p.price}<span style={{ fontSize: 10, fontWeight: 600 }}>{p.unit}</span></div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div><label style={label}>Nombre completo *</label><input style={input} value={form.nombre} onChange={set("nombre")} data-testid="f-nombre" /></div>
            <div><label style={label}>Celular *</label><input style={input} type="tel" placeholder="+52 ..." value={form.celular} onChange={set("celular")} data-testid="f-celular" /></div>
            <div><label style={label}>Email *</label><input style={input} type="email" value={form.email} onChange={set("email")} data-testid="f-email" /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={label}>Ciudad *</label><input style={input} value={form.ciudad} onChange={set("ciudad")} data-testid="f-ciudad" /></div>
              <div><label style={label}>Estado *</label><input style={input} value={form.estado} onChange={set("estado")} data-testid="f-estado" /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={label}>N.º de beneficiarios</label><input style={input} type="number" min={0} max={10} value={form.num_beneficiarios} onChange={set("num_beneficiarios")} data-testid="f-benef" /></div>
              <div><label style={label}>Código (opcional)</label><input style={input} value={form.codigo_vendedor} onChange={set("codigo_vendedor")} data-testid="f-codigo" placeholder="Vendedor / promo" /></div>
            </div>
            {addons.length > 0 && (
              <div data-testid="order-bumps" style={{ border: `2px dashed ${GREEN}`, borderRadius: 14, padding: "14px 16px", background: "#F4FCE8" }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: NAVY, marginBottom: 8 }}>✨ Mejora tu protección (opcional)</div>
                {addons.map((a) => {
                  const active = selectedAddons.includes(a.code);
                  const suffix = planType === "mensual" ? "/mes" : "/año";
                  const amount = planType === "mensual" ? a.price_monthly : a.price_monthly * 12;
                  return (
                    <label key={a.code} data-testid={`bump-${a.code}`}
                      style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                      <input type="checkbox" checked={active} onChange={() => toggleAddon(a.code)} data-testid={`bump-check-${a.code}`} style={{ marginTop: 3 }} />
                      <span style={{ flex: 1 }}>
                        <span style={{ fontWeight: 700, color: NAVY, fontSize: 14 }}>{a.label}</span>
                        <span style={{ color: GREEN, fontWeight: 800 }}> +${amount}{suffix}</span>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>{a.desc}</div>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <label style={{ display: "flex", gap: 10, fontSize: 13, color: "#374151", alignItems: "flex-start" }}>
              <input type="checkbox" checked={form.acepta_terminos} onChange={set("acepta_terminos")} data-testid="f-terminos" style={{ marginTop: 3 }} />
              <span>Acepto los <a href="/terminos" target="_blank" rel="noreferrer" style={{ color: NAVY }}>términos y condiciones</a>.</span>
            </label>
            <label style={{ display: "flex", gap: 10, fontSize: 13, color: "#374151", alignItems: "flex-start" }}>
              <input type="checkbox" checked={form.acepta_privacidad} onChange={set("acepta_privacidad")} data-testid="f-privacidad" style={{ marginTop: 3 }} />
              <span>Acepto el <a href="/privacidad" target="_blank" rel="noreferrer" style={{ color: NAVY }}>aviso de privacidad</a>.</span>
            </label>
            <label style={{ display: "flex", gap: 10, fontSize: 13, color: "#374151", alignItems: "flex-start" }}>
              <input type="checkbox" checked={form.acepta_mensajes} onChange={set("acepta_mensajes")} data-testid="f-mensajes" style={{ marginTop: 3 }} />
              <span>Autorizo recibir mensajes por WhatsApp, SMS y email sobre mi membresía.</span>
            </label>
            <button type="submit" disabled={loading} data-testid="f-submit"
              style={{ background: GREEN, color: NAVY, border: "none", padding: "17px", borderRadius: 999, fontSize: 17, fontWeight: 800, cursor: "pointer", marginTop: 4 }}>
              {loading ? "Procesando…" : (BTN_LABEL[planType] || "Pagar y activar")}
            </button>
            <div style={{ textAlign: "center", fontSize: 12, color: "#9ca3af" }}>
              <i className="fas fa-lock" /> Pago protegido con Stripe
            </div>
          </form>
        </div>
      </section>

      {/* BANNER: Programa de Socios */}
      <section style={{ background: GREEN, padding: "30px 20px", textAlign: "center" }} data-testid="socios-banner">
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: NAVY, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
            <i className="fas fa-hand-holding-dollar" /> Programa de Socios
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: NAVY, margin: "0 0 8px" }}>
            Gana dinero recomendando Mi Ángel Asís
          </h2>
          <p style={{ color: NAVY, opacity: 0.85, fontSize: 15, margin: "0 auto 18px", maxWidth: 520 }}>
            Comparte tu link, ayuda a más familias a protegerse y recibe comisiones por cada membresía. Sin costo, para creadores y afiliados.
          </p>
          <a href="/socios/registro" data-testid="socios-banner-cta"
            style={{ display: "inline-block", background: NAVY, color: "white", textDecoration: "none", padding: "14px 30px", borderRadius: 999, fontSize: 16, fontWeight: 800, boxShadow: "0 8px 24px rgba(19,42,99,0.3)" }}>
            Únete como Socio →
          </a>
        </div>
      </section>

      <footer style={{ background: NAVY, color: "rgba(255,255,255,0.75)", padding: "22px 20px", textAlign: "center", fontSize: 13 }}>
        Mi Ángel Asís © {new Date().getFullYear()} · <a href="/privacidad" style={{ color: GREEN }}>Privacidad</a> · <a href="/cuenta/login" style={{ color: GREEN }}>Ya soy miembro</a> · <a href="/cuenta/consultar" data-testid="footer-consultar-folio" style={{ color: GREEN }}>Consulta tu membresía por folio</a>
      </footer>
    </div>
  );
}
